/**
 * global
 */

/**
 * project
 */
import type React from 'react';
import { type PropsWithChildren, type ReactNode } from 'react';

import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import type { DialogProps, SxProps, Theme } from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography
} from '@mui/material';

import CloseIcon from 'components/icons/Close';
import { hasValue } from 'utils/values';

/**
 * local
 */

interface IDialogActionButton {
  /**
   * Translate key or default
   */
  title?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  disabled?: boolean;
}

export interface BaseDialogProps extends Pick<DialogProps, 'maxWidth' | 'fullWidth' | 'open' | 'id'> {
  /**
   * Translate key or default
   */
  title: string;
  sx?: SxProps<Theme>;
  progress?: boolean;
  loading?: boolean;
  // todo?: make flat
  actions?: {
    cancel?: IDialogActionButton;
    confirm?: IDialogActionButton;
  };
  hideTitleClose?: boolean;
  useDividers?: boolean;

  /** todo?: Implement as Compound components indested */
  customSlots?: {
    DialogActions?: ReactNode;
  };

  // todo?: some modals use 'as='form'' prop, with a 'control' prop.
  // as | component

  /** @todo Confirmation Dialog. Implement the error */
  error?: string | null;
}

export const BASE_DIALOG_TEST_ID = {
  title: 'baseDialogTitle',
  close: 'baseDialogClose'
} as const;

export const BaseDialog = ({
  title,
  children,
  open,
  actions,
  loading,
  progress,
  hideTitleClose,
  customSlots,
  sx,
  useDividers,
  ...other
}: PropsWithChildren<BaseDialogProps>) => {
  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (actions?.cancel?.onClick) {
      actions?.cancel?.onClick(e);
    }
  };

  const handleOk = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (actions?.confirm?.onClick) {
      actions?.confirm?.onClick(e);
    }
  };
  const px = 3;

  return (
    <Dialog className='BaseDialog' sx={sx} open={open} onClose={handleCancel} {...other}>
      <DialogTitle
        sx={{
          px: (theme) => theme.spacing(px)
        }}
      >
        <Typography data-testid={BASE_DIALOG_TEST_ID.title} variant='h6' component='div'>
          {title}
        </Typography>
        {!hideTitleClose ? (
          <IconButton
            data-testid={BASE_DIALOG_TEST_ID.close}
            disabled={actions?.cancel?.disabled}
            aria-label='close'
            onClick={handleCancel}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>

      <DialogContent sx={{ px: (theme) => theme.spacing(px), py: (theme) => theme.spacing(1) }} dividers={useDividers}>
        {children}
      </DialogContent>

      {hasValue(customSlots?.DialogActions) && <DialogActions>{customSlots.DialogActions}</DialogActions>}

      {!hasValue(customSlots?.DialogActions) && (actions?.cancel || actions?.confirm) && (
        <DialogActions sx={{ px: (theme) => theme.spacing(px) }}>
          {actions?.cancel && (
            <Button onClick={handleCancel} disabled={loading || actions?.cancel?.disabled} variant='outlined'>
              {actions?.cancel?.title || 'Close'}
            </Button>
          )}

          {actions?.confirm && (
            <>
              {loading && (
                <LoadingButton loading loadingPosition='start' startIcon={<SaveIcon />} variant='outlined'>
                  {actions?.confirm?.title || 'Confirm'}
                </LoadingButton>
              )}
              {!loading && (
                <Button onClick={handleOk} disabled={actions?.confirm?.disabled} variant='contained'>
                  {actions?.confirm?.title || 'Confirm'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      )}

      {progress && (
        <Box sx={{ width: '100%', position: 'absolute', bottom: 0 }}>
          {/* todo: color */}
          <LinearProgress />
        </Box>
      )}
    </Dialog>
  );
};
